import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  Heart,
  Sparkles,
  PiggyBank,
  BookOpen,
  Bed,
  Shirt,
  Cat,
  Lamp,
  Layers,
  Coffee,
  NotebookPen,
  CheckCircle2,
  Clock,
  Gift,
  X
} from 'lucide-react';
import { useLab, studentBase, resolveLabChildId } from '../LabContext';
import { LabLayout, Card, ParentPreviewBadge } from '../LabUI';
import { formatNims } from '../utils/nims';

// Same-origin fall-through resolver, mirrors LabContext.
const resolveStudentApi = () => {
  const path = '/api/lab/students';
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return `${configured || ''}${path}`;
  if (!configured) return path;
  try {
    const u = new URL(configured);
    if (u.origin === window.location.origin) return `${configured}${path}`;
  } catch (_e) { /* same-origin */ }
  return path;
};
const STUDENT_API = resolveStudentApi();

const CATEGORY_ICON = {
  book: BookOpen,
  blanket: Bed,
  pajamas: Shirt,
  toy: Cat,
  light: Lamp,
  bedding: Layers,
  mug: Coffee,
  notebook: NotebookPen,
  tshirt: Shirt,
  hoodie: Shirt
};

// Map prize id -> static product photo. Frontend-side mapping only; the
// backend catalog's imageUrl remains the source of truth when set.
const PRIZE_IMAGE_BY_ID = {
  'nimny-bedtime-stories': '/assets/prizes/nimny-bedtime-stories.webp',
  'nimny-cozy-blanket': '/assets/prizes/nimny-cozy-blanket.webp',
  'nimny-dream-pajamas': '/assets/prizes/nimny-dream-pajamas.webp',
  'nimny-sleep-companion': '/assets/prizes/nimny-sleep-companion.webp',
  'nimny-night-light': '/assets/prizes/nimny-night-light.webp',
  'nimny-bedding-set': '/assets/prizes/nimny-bedding-set.webp',
  'nimny-bedding-set-v2': '/assets/prizes/nimny-bedding-set-v2.webp',
  'nimny-mug': '/assets/prizes/nimny-mug.webp',
  'nimny-notebook': '/assets/prizes/nimny-notebook.webp',
  'nimny-tshirt': '/assets/prizes/nimny-tshirt.webp',
  'nimny-hoodie': '/assets/prizes/nimny-hoodie.webp'
};

const resolvePrizeImage = (prize) => {
  if (!prize) return null;
  if (prize.imageUrl) return prize.imageUrl;
  return PRIZE_IMAGE_BY_ID[prize.id] || null;
};

// Map English category to a soft accent class for the icon panel.
const CATEGORY_ACCENT = {
  book: 'from-amber-300/15 to-amber-300/5 text-amber-200',
  blanket: 'from-rose-300/15 to-rose-300/5 text-rose-200',
  pajamas: 'from-sky-300/15 to-sky-300/5 text-sky-200',
  toy: 'from-violet-300/15 to-violet-300/5 text-violet-200',
  light: 'from-yellow-300/20 to-yellow-300/5 text-yellow-200',
  bedding: 'from-emerald-300/15 to-emerald-300/5 text-emerald-200',
  mug: 'from-orange-300/15 to-orange-300/5 text-orange-200',
  notebook: 'from-teal-300/15 to-teal-300/5 text-teal-200',
  tshirt: 'from-pink-300/15 to-pink-300/5 text-pink-200',
  hoodie: 'from-indigo-300/15 to-indigo-300/5 text-indigo-200'
};

const apiGet = async (url) => {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed');
  return r.json();
};

const apiPost = async (url, body) => {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    const detail = j.detail;
    if (detail && typeof detail === 'object' && detail.code) throw detail;
    throw new Error(typeof detail === 'string' ? detail : 'Request failed');
  }
  return r.json();
};

const apiDelete = async (url) => {
  const r = await fetch(url, { method: 'DELETE', credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed');
  return r.json();
};

// Pick the most-relevant pending/active request for a given prize id.
const findActiveRequest = (requests, prizeId) => {
  if (!Array.isArray(requests)) return null;
  return (
    requests.find((r) => r.prizeId === prizeId && r.status === 'pending') ||
    requests.find((r) => r.prizeId === prizeId && r.status === 'approved') ||
    requests.find((r) => r.prizeId === prizeId && r.status === 'fulfilled') ||
    null
  );
};

const NimnyPrizeShop = () => {
  const { user, language } = useLab();
  const params = useParams();
  const location = useLocation();
  const studentId = resolveLabChildId({
    routeStudentId: params.studentId,
    pathname: location.pathname,
    user,
  });
  const base = studentBase(studentId);
  // Parent / super-admin previewing the shop never mutates the child's
  // wishlist or creates a request; the page renders read-only and shows
  // a small badge so it's obvious this is a preview, not a child action.
  const isParent = user?.role === 'parent' || user?.role === 'super_admin';

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmPrize, setConfirmPrize] = useState(null);
  const [toast, setToast] = useState('');

  const tx = useCallback(
    (ru, en) => (language === 'ru' ? ru : en),
    [language]
  );

  const reload = useCallback(async () => {
    try {
      const body = await apiGet(
        `${STUDENT_API}/${encodeURIComponent(studentId)}/prize-shop`
      );
      setData(body);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { reload(); }, [reload]);

  const closestGoal = useMemo(() => {
    if (!data) return null;
    const balance = data.balance || 0;
    const goals = (data.catalog || [])
      .filter((p) => !p.affordable)
      .sort((a, b) => a.costNims - b.costNims);
    return goals[0] || null;
  }, [data]);

  const hasAffordable = useMemo(
    () => (data?.catalog || []).some((p) => p.affordable),
    [data]
  );

  const onToggleFavorite = async (prize) => {
    if (busyId) return;
    if (isParent) return; // read-only preview for parent
    setBusyId(prize.id);
    try {
      if (prize.isFavorite) {
        await apiDelete(
          `${STUDENT_API}/${encodeURIComponent(studentId)}/prize-shop/favorites/${prize.id}`
        );
      } else {
        await apiPost(
          `${STUDENT_API}/${encodeURIComponent(studentId)}/prize-shop/favorites`,
          { prizeId: prize.id }
        );
      }
      await reload();
    } catch (e) {
      setError(e.message || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const onConfirmRequest = async () => {
    const prize = confirmPrize;
    if (!prize || busyId) return;
    if (isParent) { setConfirmPrize(null); return; } // read-only for parent
    setBusyId(prize.id);
    try {
      await apiPost(
        `${STUDENT_API}/${encodeURIComponent(studentId)}/prize-shop/requests`,
        { prizeId: prize.id }
      );
      setToast(tx('Запрос отправлен родителю.', 'Request sent to parent.'));
      setConfirmPrize(null);
      await reload();
    } catch (e) {
      if (e && e.code === 'insufficient_nims') {
        setError(
          tx(
            `Пока не хватает ${e.missing} нимов.`,
            `${e.missing} more nims needed.`
          )
        );
      } else {
        setError((e && e.message) || 'Failed');
      }
    } finally {
      setBusyId(null);
    }
  };

  const balance = data?.balance ?? 0;

  return (
    <LabLayout desktopWide>
      <Link
        to={base}
        className="inline-flex items-center gap-1.5 text-amber-200/75 hover:text-amber-200 text-sm font-sans"
        data-testid="prize-shop-back"
      >
        <ChevronLeft className="w-4 h-4" />
        {tx('Назад', 'Back')}
      </Link>

      <section className="mt-3" data-testid="prize-shop-header">
        <h1
          className="text-3xl text-[#f3d9a4]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="prize-shop-title"
        >
          {tx('Лавка Нимни', 'Nimny Prize Shop')}
        </h1>
        <ParentPreviewBadge />
        <p
          className="mt-1 text-amber-200/65 font-serif text-sm"
          data-testid="prize-shop-subtitle"
        >
          {tx(
            'Копи нимы, выбирай призы и попроси родителя подтвердить.',
            'Save nims, choose prizes, and ask your parent to confirm.'
          )}
        </p>
      </section>

      {/* Balance + closest goal */}
      <Card className="mt-5 p-4 lg:max-w-2xl" data-testid="prize-shop-balance-card">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-amber-300/12 border border-amber-300/20 flex items-center justify-center text-amber-200">
            <PiggyBank className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-amber-200/55 text-[10px] uppercase tracking-wider font-sans">
              {tx('В Копилке', 'In Kopilka')}
            </div>
            <div
              className="text-[#f3d9a4] text-2xl"
              style={{ fontFamily: 'var(--font-heading)' }}
              data-testid="prize-shop-balance"
            >
              {formatNims(balance)}
            </div>
          </div>
        </div>
        {hasAffordable ? (
          <div
            className="mt-3 text-emerald-300/85 text-sm font-sans flex items-center gap-1.5"
            data-testid="prize-shop-can-pick"
          >
            <Sparkles className="w-4 h-4" />
            {tx('Уже можно выбрать приз.', 'You can already choose a prize.')}
          </div>
        ) : closestGoal ? (
          <div
            className="mt-3 text-amber-200/70 text-sm font-sans"
            data-testid="prize-shop-closest-goal"
          >
            {tx(
              `Ближайшая цель: ${closestGoal.title?.ru || closestGoal.title?.en}, ещё ${closestGoal.missingNims} нимов.`,
              `Closest goal: ${closestGoal.title?.en || closestGoal.title?.ru}, ${closestGoal.missingNims} more nims.`
            )}
          </div>
        ) : null}
      </Card>

      {error && (
        <div
          className="mt-4 rounded-xl border border-red-300/25 bg-red-400/10 text-red-200 text-sm px-4 py-2.5 font-sans"
          data-testid="prize-shop-error"
        >
          {error}
        </div>
      )}
      {toast && (
        <div
          className="mt-4 rounded-xl border border-emerald-300/25 bg-emerald-400/10 text-emerald-200 text-sm px-4 py-2.5 font-sans flex items-center gap-2"
          data-testid="prize-shop-toast"
        >
          <CheckCircle2 className="w-4 h-4" />
          {toast}
        </div>
      )}

      {/* Catalog grid */}
      {loading && (
        <div className="mt-6 text-amber-200/55 text-sm font-sans" data-testid="prize-shop-loading">
          {tx('Загрузка…', 'Loading…')}
        </div>
      )}

      {!loading && (
        <div
          className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="prize-shop-grid"
        >
          {(data?.catalog || []).map((prize) => {
            const Icon = CATEGORY_ICON[prize.category] || Gift;
            const accent = CATEGORY_ACCENT[prize.category] || CATEGORY_ACCENT.toy;
            const imageSrc = resolvePrizeImage(prize);
            const activeRequest = findActiveRequest(data?.requests, prize.id);
            return (
              <Card
                key={prize.id}
                className="p-4 flex flex-col"
                data-testid={`prize-card-${prize.id}`}
              >
                {/* Product visual: real photo when available, icon panel otherwise. */}
                {imageSrc ? (
                  <div
                    className="h-48 sm:h-56 lg:h-64 rounded-2xl overflow-hidden border border-amber-300/15 mb-3 relative bg-[#0a0f26]/60"
                    data-testid={`prize-card-${prize.id}-image`}
                  >
                    <img
                      src={imageSrc}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  </div>
                ) : (
                  <div
                    className={
                      'h-48 sm:h-56 lg:h-64 rounded-2xl bg-gradient-to-br border border-amber-300/10 flex items-center justify-center mb-3 ' +
                      accent
                    }
                    aria-hidden
                  >
                    <Icon className="w-12 h-12" />
                  </div>
                )}

                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-[#f3d9a4] text-base"
                      style={{ fontFamily: 'var(--font-heading)' }}
                      data-testid={`prize-card-${prize.id}-title`}
                    >
                      {tx(prize.title?.ru, prize.title?.en) || prize.id}
                    </h3>
                    <p className="mt-1 text-amber-200/65 text-xs font-serif">
                      {tx(prize.description?.ru, prize.description?.en) || ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleFavorite(prize)}
                    disabled={busyId === prize.id || isParent}
                    title={isParent ? (tx('Просмотр родителя', 'Parent preview')) : undefined}
                    className={
                      'shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-colors ' +
                      (prize.isFavorite
                        ? 'border-rose-300/40 text-rose-300 bg-rose-400/10'
                        : 'border-amber-300/20 text-amber-200/65 hover:bg-amber-300/5') +
                      (isParent ? ' opacity-60 cursor-not-allowed' : '')
                    }
                    aria-label={
                      prize.isFavorite
                        ? tx('Убрать из желаний', 'Remove from wishes')
                        : tx('В желания', 'Add to wishes')
                    }
                    data-testid={`prize-card-${prize.id}-favorite`}
                    data-favorite={prize.isFavorite ? 'on' : 'off'}
                  >
                    <Heart
                      className="w-4 h-4"
                      fill={prize.isFavorite ? 'currentColor' : 'none'}
                    />
                  </button>
                </div>

                {/* Cost */}
                <div className="mt-3 flex items-center justify-between">
                  <span
                    className="inline-flex items-center gap-1.5 text-[#f3d9a4] font-sans text-sm"
                    data-testid={`prize-card-${prize.id}-cost`}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                    {formatNims(prize.costNims)}
                  </span>
                  {prize.isFavorite && (
                    <span
                      className="text-[10px] uppercase tracking-wider text-rose-200/85 font-sans"
                      data-testid={`prize-card-${prize.id}-wishes-tag`}
                    >
                      {tx('В желаниях', 'In wishes')}
                    </span>
                  )}
                </div>

                {/* Status / CTA */}
                <div className="mt-3">
                  {activeRequest ? (
                    <RequestStatusPill request={activeRequest} tx={tx} />
                  ) : isParent ? (
                    <div
                      className="w-full text-center py-2.5 rounded-2xl border border-amber-300/15 text-amber-200/55 text-xs font-sans"
                      data-testid={`prize-card-${prize.id}-parent-preview`}
                    >
                      {tx('Просмотр родителя', 'Parent preview')}
                    </div>
                  ) : prize.affordable ? (
                    <button
                      type="button"
                      onClick={() => setConfirmPrize(prize)}
                      disabled={busyId === prize.id}
                      className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] disabled:opacity-40 disabled:cursor-not-allowed text-[#1a1208] font-sans text-sm transition-colors"
                      data-testid={`prize-card-${prize.id}-request`}
                    >
                      <Gift className="w-4 h-4" />
                      {tx('Обменять нимы', 'Exchange nims')}
                    </button>
                  ) : (
                    <div
                      className="w-full text-center py-2.5 rounded-2xl border border-amber-300/15 text-amber-200/55 text-sm font-sans"
                      data-testid={`prize-card-${prize.id}-needs-more`}
                    >
                      {tx(
                        `Пока не хватает ${prize.missingNims} нимов`,
                        `${prize.missingNims} more nims needed`
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirmation modal */}
      {confirmPrize && (
        <ConfirmRequestModal
          prize={confirmPrize}
          tx={tx}
          busy={busyId === confirmPrize.id}
          onCancel={() => setConfirmPrize(null)}
          onConfirm={onConfirmRequest}
        />
      )}
    </LabLayout>
  );
};

const RequestStatusPill = ({ request, tx }) => {
  const map = {
    pending: {
      label: tx('Ожидает родителя', 'Waiting for parent'),
      icon: Clock,
      className: 'border-amber-300/30 text-amber-200 bg-amber-300/8'
    },
    approved: {
      label: tx('Подтверждено', 'Approved'),
      icon: CheckCircle2,
      className: 'border-emerald-300/30 text-emerald-200 bg-emerald-300/10'
    },
    fulfilled: {
      label: tx('Вручено', 'Received'),
      icon: Gift,
      className: 'border-sky-300/30 text-sky-200 bg-sky-300/10'
    },
    rejected: {
      label: tx('Отклонено', 'Not approved'),
      icon: X,
      className: 'border-amber-300/15 text-amber-200/55 bg-transparent'
    },
    cancelled: {
      label: tx('Отменено', 'Cancelled'),
      icon: X,
      className: 'border-amber-300/15 text-amber-200/55 bg-transparent'
    }
  };
  const cfg = map[request.status] || map.pending;
  const Icon = cfg.icon;
  return (
    <div
      className={
        'w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-2xl border text-sm font-sans ' +
        cfg.className
      }
      data-testid={`prize-card-status-${request.status}`}
      data-request-status={request.status}
    >
      <Icon className="w-4 h-4" />
      {cfg.label}
    </div>
  );
};

const ConfirmRequestModal = ({ prize, tx, busy, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-[2px]"
    onClick={onCancel}
    data-testid="prize-confirm-overlay"
  >
    <div
      className="w-full max-w-md rounded-2xl border border-amber-300/20 bg-[#0a0f24]/95 p-5 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
      data-testid="prize-confirm-modal"
    >
      <div className="text-amber-200/65 text-xs uppercase tracking-wider font-sans">
        {tx('Запрос приза', 'Prize request')}
      </div>
      <h2
        className="mt-1 text-xl text-[#f3d9a4]"
        style={{ fontFamily: 'var(--font-heading)' }}
        data-testid="prize-confirm-title"
      >
        {tx(prize.title?.ru, prize.title?.en) || prize.id}
      </h2>
      <p className="mt-2 text-[#f5ebe0]/85 text-sm font-sans">
        {tx('Отправить запрос родителю?', 'Send request to parent?')}
      </p>
      <p className="mt-2 text-amber-200/65 text-xs font-serif">
        {tx(
          'Родитель проверит и подтвердит приз. Нимы спишутся только после подтверждения.',
          'Your parent will check and confirm the prize. Nims are deducted only after approval.'
        )}
      </p>
      <div className="mt-5 flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-2.5 rounded-2xl border border-amber-300/20 text-amber-200/85 hover:bg-amber-300/5 text-sm font-sans"
          data-testid="prize-confirm-cancel"
        >
          {tx('Отмена', 'Cancel')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="flex-1 py-2.5 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] disabled:opacity-40 text-[#1a1208] text-sm font-sans inline-flex items-center justify-center gap-2"
          data-testid="prize-confirm-submit"
        >
          <Gift className="w-4 h-4" />
          {busy
            ? tx('Отправляем…', 'Sending…')
            : tx('Отправить запрос', 'Send request')}
        </button>
      </div>
    </div>
  </div>
);

export default NimnyPrizeShop;
